import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchField from '@hkm/components/shared/Fields/SearchField/SearchField';

import { ConfigurationUser, TaskUser } from '@ac/library-api';
import {
  AcButton,
  AcButtonPattern
} from '@ac/mobile-components/dist/components/button';
import { AcButtonContent } from '@ac/mobile-components/dist/components/button-content';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import {
  AcModal,
  AcModalBody,
  AcModalFooter,
  AcModalHeader,
  AcModalPattern
} from '@ac/mobile-components/dist/components/modal';
import { FlexDirection, Icon } from '@ac/mobile-components/dist/enums';

import './../TaskModals.css';

export interface ComponentProps {
  isVisible: boolean;
  usersToSelect: ConfigurationUser[];
  onUsersSearch: (searchValue: string) => void;
  onClearUsersSearch: () => void;
  onChange: (user: TaskUser) => void;
  onClose: () => void;
}

const TaskAssignToUserModal: FC<ComponentProps> = (props: ComponentProps) => {
  const { t } = useTranslation();
  const {
    usersToSelect,
    isVisible,
    onClose,
    onUsersSearch,
    onChange,
    onClearUsersSearch
  } = props;
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();

  const searchOptions = useMemo(() => {
    return usersToSelect.map(user => ({
      id: user.id,
      text: `${user.personalData?.firstName} ${user.personalData?.lastName}`
    }));
  }, [usersToSelect]);

  const clearModal = useCallback(() => {
    setSelectedUserId(undefined);
  }, []);

  const closeModal = useCallback(() => {
    clearModal();
    onClose();
  }, [clearModal, onClose]);

  const onUserAssign = useCallback(() => {
    if (!selectedUserId) {
      return;
    }
    const user = usersToSelect.find(({ id }) => id === selectedUserId);
    onChange({
      firstName: user?.personalData?.firstName || '',
      lastName: user?.personalData?.lastName || '',
      id: selectedUserId || '',
      userName: user?.userName || ''
    });
    clearModal();
  }, [onChange, selectedUserId, usersToSelect]);

  return (
    <AcModal
      className="task-modal"
      isOpen={isVisible}
      onClose={closeModal}
      pattern={AcModalPattern.Drawer}
    >
      <AcModalHeader showCloseButton className="task-modal-header">
        {t('TASK_MANAGEMENT.DASHBOARD.OPTIONS.ASSIGN_TO_USER')}
      </AcModalHeader>
      <AcModalBody className="modal-body">
        <SearchField
          className="ac-spacing-top-sm"
          optionsArray={searchOptions}
          label={t('TASK_MANAGEMENT.MODALS.SEARCH_FOR_USER')}
          onSearch={onUsersSearch}
          onOptionSelect={setSelectedUserId}
          onClearSelection={onClearUsersSearch}
        />
      </AcModalBody>
      <AcModalFooter className="task-modal-footer">
        <AcFlex
          className="full-width footer-buttons"
          direction={FlexDirection.column}
          grow={true}
        >
          <AcButton className="full-width" onClick={onUserAssign}>
            <AcButtonContent text={t('TASK_MANAGEMENT.MODALS.ASSIGN')} />
          </AcButton>
          <AcButton
            pattern={AcButtonPattern.Tertiary}
            className="full-width"
            onClick={closeModal}
          >
            <AcButtonContent icon={Icon.Cancel} text={t('GLOBAL.CANCEL')} />
          </AcButton>
        </AcFlex>
      </AcModalFooter>
    </AcModal>
  );
};

export default memo(TaskAssignToUserModal);



// WEBPACK FOOTER //
// ./src/components/TaskManagement/shared/components/Modals/TaskAssignToUserModal/TaskAssignToUserModal.tsx